<template lang="pug">
  ul(:class="`wrapper-radio-slider-${theme}`")
    span(:style="positionBackground")

    li.to-click(
      v-for='(tab, index) in items',
      :class="{ 'is-active': index === current }",
      :key="index"
      tabindex="0"
      @click='selecionarAba(tab, index)'
      @keyup.enter='selecionarAba(tab, index)'
    )
      a.noselect(:class="{disabled: disabledModes.includes(tab.id), required: !!tab.required}")

        slot(:tab="tab")
          | {{ tab.name }}
        .icone-chapado-info.color-info(v-if="observacao[tab.id]" v-ptt-popover.down.hover="{content: observacao[tab.id]}")

</template>

<script>
export default {
  name: 'RadioSlider',
  props: {
    current: {type: Number, required: true},
    items: {type: Array, required: true},
    disabledModes: {type: Array, required: false, 'default': () => []},
    theme: {type: String, default: 'default'},
    observacao: {type: Object, required: false, default: () => ({})}
  },
  computed: {
    positionBackground () {
      if (this.theme === 'segmentado') {
        // Para o tema segmentado o span precisa cobrir exatamente 1 célula.
        // O trilho tem padding: 3px em cada lado; descontamos 6px do cálculo
        // para que span e células do grid coincidam pixel a pixel.
        const n = this.items.length || 1
        return {
          left: `calc(${this.current} * (100% - 6px) / ${n} + 3px)`,
          width: `calc((100% - 6px) / ${n})`
        }
      }
      return {left: `${this.current}00%`}
    }
  },
  methods: {
    selecionarAba (tab, index) {
      if (index !== this.current && !this.disabledModes.includes(this.items[index].id)) {
        this.$emit('selected', tab, index)
      }
    }
  }
}
</script>

<style scoped lang="scss">
// ─── Tema default → grupo de ação do DS (doc 16 §5.5) ──────────────────────
// Converge com o tema segmentado: trilho pílula neutro + thumb branco elevado.
// Fora do tema segmentado, positionBackground() devolve apenas { left: "N00%" };
// o deslocamento por célula depende de grid-column (a célula do grid vira o
// bloco-contêiner do thumb) + width: 100% — mecanismo preservado da versão antiga.
.wrapper-radio-slider-default {
  list-style-type: none;
  display: grid;
  // Colunas iguais que encolhem até caber (minmax com piso 0). Com piso 100px o
  // trilho não cabia em contêiner estreito (layout 2-col ≤ ~1024) e a última aba
  // vazava; o thumb desliza por left:N00% = N×largura-de-coluna, então segue alinhado.
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  margin: 0;
  padding: 3px;
  position: relative;
  height: 52px;
  background: var(--cor-neutra-10);
  border: 1px solid var(--cor-neutra-20);
  border-radius: var(--arredondamento-max);

  span {
    position: absolute;
    top: 3px;
    height: calc(100% - 6px);
    width: 100%;
    grid-column: 1 / 1;
    background: var(--cor-neutra-0);
    border-radius: var(--arredondamento-max);
    box-shadow: 0 1px 2px color-mix(in srgb, var(--texto-forte) 12%, transparent);
    z-index: 0;
    transition: left 0.2s ease-out;
  }

  li {
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--arredondamento-max);
    min-width: 0;
    padding-inline: 8px;

    a {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      font-size: 14px;
      font-weight: 500;
      line-height: 16px;
      color: var(--cor-neutra-200);
      text-align: center;
      transition: color 0.15s ease-out;

      // Ate duas linhas para rotulos longos sem quebrar a altura do controle.
      white-space: normal;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;

      &.disabled {
        color: var(--cor-neutra-70);
        cursor: default;
      }

      &.required {
        &::after {
          content: " *";
          color: var(--color-danger);
          font-size: 0.7em;
          position: relative;
          bottom: 3px;
          right: 3px;
        }
      }
    }

    &:focus-visible {
      outline: 2px solid var(--cor-secundaria-300);
      outline-offset: 2px;
    }
  }

  li.is-active {
    a {
      color: var(--cor-secundaria-300);
      font-weight: 600;
    }
  }
}

// ─── Tema custom-rules → abas de conteúdo com sublinha (doc 16 §5.5) ────────
// Permanece como sublinha de abas (até eventual migração p/ PontotelAbas, que
// não existe no DS instalado); apenas as cores foram tokenizadas: ativo em
// secundaria-100, divisor/estados em neutras do DS.
.wrapper-radio-slider-custom-rules {
  list-style-type: none;
  display: grid;
  margin: 0;
  padding: 0 3px;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  position: relative;
  border-bottom: 1px solid var(--cor-neutra-20);

  span {
    position: absolute;
    width: 100%;
    height: 5%;
    top: 95%;
    grid-column: 1 / 1;
    background-color: var(--cor-secundaria-100);
    z-index: 0;
    transition: left 0.2s ease-out;
  }

  li {
    z-index: 1;
    padding: 5px 12px 5px 12px;
    display: grid;

    a {
      color: var(--cor-neutra-300);
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 16px;
      font-weight: 400;
      line-height: 1.25;
      text-align: center;
      transition: all 0.2s linear;

      &.disabled {
        color: var(--cor-neutra-70);
        cursor: default;
      }

      &.required::after {
        content: " *";
        color: var(--color-danger);
        font-size: 0.7em;
        position: relative;
        bottom: 3px;
        right: 3px;
      }
    }
  }
  li:hover:not(.is-active) {
    // background-color: var(--color-default-hover) !important;
  }

  li.is-active {
    a {
      color: var(--cor-secundaria-100);
      font-weight: 600;
    }
  }
}

// ─── Tema segmentado (opt-in via theme="segmentado") ───────────────────────
// Grupo de ação do DS (doc 16 §5.5): trilho pílula neutro + thumb branco
// elevado — mesmo visual do tema default. No tema segmentado, positionBackground()
// fornece left+width inline (o thumb cobre exatamente 1 célula).
.wrapper-radio-slider-segmentado {
  list-style-type: none;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  margin: 0;
  padding: 3px;
  position: relative;
  height: 52px;
  background: var(--cor-neutra-10);
  border: 1px solid var(--cor-neutra-20);
  border-radius: var(--arredondamento-max);

  // thumb deslizante — posição e largura controladas por positionBackground()
  span {
    position: absolute;
    top: 3px;
    height: calc(100% - 6px);
    background: var(--cor-neutra-0);
    border-radius: var(--arredondamento-max);
    box-shadow: 0 1px 2px color-mix(in srgb, var(--texto-forte) 12%, transparent);
    z-index: 0;
    transition: left 0.2s ease-out;
  }

  li {
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--arredondamento-max);
    min-width: 0;
    padding-inline: 8px;

    a {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      font-size: 14px;
      font-weight: 500;
      line-height: 16px;
      color: var(--cor-neutra-200);
      text-align: center;
      transition: color 0.15s ease-out;

      // Ate duas linhas para rotulos longos sem quebrar a altura do controle.
      white-space: normal;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;

      &.disabled {
        color: var(--cor-neutra-70);
        cursor: default;
      }

      &.required {
        &::after {
          content: " *";
          color: var(--color-danger);
          font-size: 0.7em;
          position: relative;
          bottom: 3px;
          right: 3px;
        }
      }
    }

    &:focus-visible {
      outline: 2px solid var(--cor-secundaria-300);
      outline-offset: 2px;
    }
  }

  li.is-active {
    a {
      color: var(--cor-secundaria-300);
      font-weight: 600;
    }
  }
}

@media (max-width: 640px) {
  .wrapper-radio-slider-default,
  .wrapper-radio-slider-segmentado,
  .wrapper-radio-slider-custom-rules {
    overflow-x: auto;
    overflow-y: hidden;
    grid-auto-columns: minmax(128px, 1fr);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .wrapper-radio-slider-custom-rules {
    scroll-snap-type: x proximity;

    li {
      scroll-snap-align: start;
      min-width: 0;
      padding-inline: 10px;
    }

    li a {
      white-space: normal;
    }
  }
}
</style>
